/**
 * T027/US5 (FR-017, SC-007). Columns for `StockMovement.tsx` — one `StockMovementRow` is a
 * `MovementView` plus `skuId`/`warehouseId` (verified against `warehouse.d.ts`), with the cause and
 * quantity cells a single SKU's movement history shows, plus the SKU column a cross-SKU report needs.
 *
 * Accessors on every column so the toolbar's search matches and a header click sorts the whole
 * fetched set; the two changes sort on the API's own figure read as a number, shown as sent.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { formatMoment } from "../../lib/format";
import { formatQty, movementKindLabel, movementSourceText, personName, reasonCodeLabel, skuLabel, type StockMovementRow } from "../../lib/services/warehouse";
import { inventoryPath } from "../../lib/paths";

/** The SKU, cause and person by the API's own labels (`skuCode`/`skuName`, `sourceLabel`, `actorName`) — never an id. */
export const stockMovementReportColumns: ColumnDef<StockMovementRow>[] = [
  // ISO timestamp — sorts chronologically as text.
  { id: "occurredAt", accessorKey: "occurredAt", header: "When", cell: ({ row }) => formatMoment(row.original.occurredAt) },
  {
    id: "sku",
    accessorFn: (row) => skuLabel(row),
    header: "SKU",
    cell: ({ row }) => (
      <Link to={inventoryPath(row.original.skuId)} className="hover:underline">
        {skuLabel(row.original)}
      </Link>
    ),
  },
  {
    id: "cause",
    accessorFn: (row) => `${movementKindLabel(row.kind)} ${movementSourceText(row)}`,
    header: "Cause",
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium">{movementKindLabel(row.original.kind)}</span>
        <span className="text-muted-foreground text-xs">
          {movementSourceText(row.original)}
        </span>
      </div>
    ),
  },
  {
    id: "qtyDelta",
    accessorFn: (row) => Number(row.qtyDelta),
    header: "Quantity change",
    meta: { align: "right" },
    cell: ({ row }) => <span className="font-mono">{formatQty(row.original.qtyDelta)}</span>,
  },
  {
    id: "reservedDelta",
    accessorFn: (row) => Number(row.reservedDelta),
    header: "Reserved change",
    meta: { align: "right" },
    cell: ({ row }) => {
      const value = row.original.reservedDelta;
      return <span className="font-mono">{Number(value) === 0 ? "—" : formatQty(value)}</span>;
    },
  },
  {
    id: "actor",
    accessorFn: (row) => (row.actorId ? personName(row.actorName) : ""),
    header: "By",
    cell: ({ row }) => (row.original.actorId ? personName(row.original.actorName) : "—"),
  },
  {
    id: "reason",
    accessorFn: (row) => (row.reasonCode ? reasonCodeLabel(row.reasonCode) : ""),
    header: "Reason",
    cell: ({ row }) => (row.original.reasonCode ? reasonCodeLabel(row.original.reasonCode) : "—"),
  },
];
